import { AutocompleteValue, Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { RatelistStatus as RatelistStatusEnum } from "@sinch/core/entities/serverEnums";
import { RatelistStatus, RatelistType } from "@sinch/core/entities/serverEnums.types";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { MdsCalendarClock, MdsCloseXs } from "@sinch/icons";
import { isNil } from "ramda";
import { isNilOrEmpty, isNotNil } from "ramda-adjunct";
import React, { ReactNode, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CompanyRatelist, RatelistList } from "./queries.graphql";
import {
  CompanyRatelistQuery,
  CompanyRatelistQueryVariables,
  RatelistListQuery,
  RatelistListQueryVariables,
} from "./queries.types";
import { MdsIcon } from "@sinch/components";
import { useRateRevision } from "@sinch/components/application/Rate/tools";
import { AutocompleteInput, AutocompleteOption } from "@sinch/components/form/AutocompleteInput/AutocompleteInput";
import { AutocompleteInputProps, AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";
import { SelectListCheckboxItem } from "@sinch/components/application/Rate/components/RatelistSelectInput/SelectListCheckboxItem";
import { Ratelist } from "@sinch/types/sinch.types";
import { useRatelistType } from "@sinch/components/application/Rate/useRatelistType";
import { useFormat } from "@sinch/core/hooks/format";
import { match } from "ts-pattern";

export interface RatelistSelectInputProps<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
> extends Omit<
    AutocompleteInputProps<AutocompleteOptions, Multiple, DisableClearable, false>,
    "value" | "onChange" | "options" | "ref"
  > {
  /**
   * Input name
   */
  value?: Multiple extends true ? (number | string)[] : number | string | null;
  onChange?: (val: AutocompleteValue<number | string, Multiple, DisableClearable, false> | null) => void;
  /**
   * Limit searched list of options
   */
  limit?: number;
  /**
   * Type of rate list to fetch (e.g. tag, transport, etc.)
   */
  ratelistType: RatelistType;
  /**
   * When provided, fetches rate lists specific to this company instead of the general list
   */
  companyId?: string;
  /**
   * ID of the rate list to pre-select when options finish loading
   */
  defaultRatelist?: string | null;
  /**
   * Restrict the fetched rate lists to company-specific ones only
   */
  companySpecific?: boolean;
  /**
   * Extra rate list IDs to always include in the options alongside the fetched list
   */
  additionalRatelists?: string[];
  name: string;
  /**
   * When true, auto-selects the rate list flagged as default if no value is set
   */
  masterDefault?: boolean;
  optionSelector?: (options: AutocompleteOptions[]) => AutocompleteOptions[];
}

/**
 * Autocomplete select input for choosing one or more rate lists. Fetches options either from
 * the general list or from a company-specific list depending on whether `companyId` is provided.
 * Supports automatic default value selection on load via `defaultRatelist`, `masterDefault`, or
 * single-option auto-select.
 */
export const RatelistSelectInput = <
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
>({
  ratelistType,
  onChange,
  value,
  loading: loadingProp,
  defaultRatelist,
  companyId,
  multiple,
  companySpecific,
  additionalRatelists,
  masterDefault,
  optionSelector,
  ...props
}: RatelistSelectInputProps<Multiple, DisableClearable>) => {
  const { t } = useTranslation(["common", "draft"]);
  const { data, loading } = useQueryHandler<RatelistListQuery, RatelistListQueryVariables>(
    RatelistList,
    {
      type: ratelistType,
      companySpecific: companySpecific,
      ratelists: additionalRatelists,
      noAdditionalRatelists: isNilOrEmpty(additionalRatelists),
    },
    { skip: isNotNil(companyId) }
  );
  const { data: companyData, loading: companyLoading } = useQueryHandler<
    CompanyRatelistQuery,
    CompanyRatelistQueryVariables
  >(
    CompanyRatelist,
    {
      type: ratelistType,
      companyId: companyId!,
      ratelists: additionalRatelists,
      noAdditionalRatelists: isNilOrEmpty(additionalRatelists),
    },
    { skip: isNil(companyId) }
  );

  // Find the ID of the rate list flagged as `default` across all available options.
  // Used as a fallback selection when no value or defaultRatelist is provided.
  const masterDefaultId = useMemo(
    () =>
      masterDefault
        ? ([
            ...((data ?? companyData)?.additionalRatelists ?? []),
            ...((isNil(companyId) ? data?.ratelists : companyData?.company?.ratelists) ?? []),
          ].find((item) => item.default)?.id ?? null)
        : null,
    [companyData, companyId, data, masterDefault]
  );

  const { getRevisionNumber } = useRateRevision();
  // Merge additionalRatelists and regular ratelists, deduplicating by ID using a Map
  // so that extra entries passed via `additionalRatelists` don't appear twice.
  const options: AutocompleteOptions[] = useMemo(() => {
    const list =
      Array.from(
        [
          ...((data ?? companyData)?.additionalRatelists ?? []),
          ...((isNil(companyId) ? data?.ratelists : companyData?.company?.ratelists) ?? []),
        ]
          .reduce(
            (map, item) => map.set(item.id, item),
            new Map<
              string,
              {
                id: string;
                name: string;
                version: number;
                status: RatelistStatus;
                type: RatelistType;
                adHoc: boolean;
                validTo: Date;
                validFrom: Date;
              }
            >()
          )
          .values()
      ).map(({ id, name, version, status, type, validTo, validFrom, adHoc: isAdhoc }) => ({
          value: id,
          label: isAdhoc ? t("Ratelist.customOverriddenRatelist") : name,
          option: (
            <RatelistOption
              adHoc={isAdhoc}
              name={name}
              status={status}
              type={type}
              validFrom={validFrom}
              validTo={validTo}
              version={version}
            />
          ),
        })) ?? [];
    return optionSelector?.(list) ?? list;
  }, [data, companyData, companyId, getRevisionNumber]);

  const handleChange: (val: AutocompleteValue<string | number, Multiple, DisableClearable, false> | null) => void = (
    val
  ) => {
    onChange?.(val);
  };

  const isLoading = loading || companyLoading || loadingProp;

  // When options finish loading, ensure a value is selected.
  // Selection priority: existing value > defaultRatelist > single available option > masterDefault.
  // queueMicrotask defers the onChange call to the next microtask tick — without it, the change
  // is sometimes not propagated to the form store in certain render sequences.
  useEffect(() => {
    if (!isLoading) {
      const val = value ? value : (defaultRatelist ?? (options.length === 1 ? options[0].value : masterDefaultId));
      queueMicrotask(() =>
        onChange?.((val ?? null) as AutocompleteValue<string | number, Multiple, DisableClearable, false>)
      );
    }
  }, [options, masterDefaultId, isLoading]);

  // When the defaultRatelist prop itself changes (e.g. parent swaps the preset), update the selection.
  useEffect(() => {
    if (!isLoading && defaultRatelist) {
      onChange?.(
        (multiple ? [defaultRatelist] : defaultRatelist) as AutocompleteValue<
          string | number,
          Multiple,
          DisableClearable,
          false
        >
      );
    }
  }, [defaultRatelist]);

  return (
    <AutocompleteInput<AutocompleteOptions, Multiple, DisableClearable, false>
      {...props}
      freeSolo={false}
      loading={isLoading}
      multiple={multiple}
      onChange={handleChange}
      openOnFocus
      options={options}
      renderOption={
        multiple
          ? (props, option, state) => (
              <SelectListCheckboxItem key={props.key} option={option} props={props} state={state} />
            )
          : (optionProps, option, _, ownerState) => (
              <AutocompleteOption
                {...optionProps}
                {...option}
                key={option.value}
                label={ownerState.getOptionLabel(option)}
              />
            )
      }
      renderTags={(vals, getTagProps) =>
        vals.map((val, index) => (
          <Chip
            {...getTagProps({ index })}
            key={val?.value || index}
            deleteIcon={<MdsIcon fontSize="xs" icon={MdsCloseXs} />}
            label={val?.option}
            sx={{ borderRadius: "4px !important" }}
          />
        ))
      }
      skeleton={loading || companyLoading || loadingProp}
      value={value}
    />
  );
};

export const RatelistOption = ({
  type,
  status,
  name,
  version,
  validTo,
  validFrom,
  includeType,
  adHoc: isAdhoc,
}: Pick<Ratelist, "type" | "status" | "name" | "version" | "validTo" | "validFrom" | "adHoc"> & {
  includeType?: boolean;
}) => {
  const { getRatelistTypeLabel } = useRatelistType();
  const { getRevisionNumber } = useRateRevision();
  const { t } = useTranslation();
  const { date } = useFormat();

  const isExpired = validTo && new Date() > validTo;
  const isUpcoming = validFrom && new Date() < validFrom;

  const validityStatus = match(true)
    .returnType<ReactNode>()
    .with(isExpired, () => (
      <Tooltip title={t("RateBuilder.expiredDescription")}>
        <Chip color="warning" label={t("expired")} size="tiny" sx={{ ml: 1, px: 0, "&>.MuiChip-label": { px: 0.5 } }} />
      </Tooltip>
    ))
    .with(isUpcoming, () => (
      <Tooltip title={t("RateBuilder.expiredDescription")}>
        <Chip color="info" label={t("upcoming")} size="tiny" sx={{ ml: 1, px: 0, "&>.MuiChip-label": { px: 0.5 } }} />
      </Tooltip>
    ))
    .otherwise(() => null);

  const indefinitelyLabel = t("indefinitely");
  return (
    <Stack
      alignItems="center"
      direction="row"
      justifyContent="space-between"
      spacing={1}
      sx={(theme) => (status === RatelistStatusEnum.Archived ? { color: theme.palette.warning.main } : {})}
      width="100%"
    >
      <Box sx={{ color: isAdhoc ? "info.main" : undefined }}>
        {isAdhoc ? t("Ratelist.customOverriddenRatelist") : name}
        {status === RatelistStatusEnum.Archived ? (
          <Typography sx={{ color: "inherit" }} variant="descriptionTiny">
            {" "}
            ({t("RateBuilder.archived")})
          </Typography>
        ) : (
          validityStatus
        )}
      </Box>
      <Stack direction="row" gap={1}>
        {(validTo || isUpcoming) && (
          <Tooltip
            title={t("RateBuilder.validityTooltip", {
              validFrom: date.mediumMonthYear(validFrom),
              validTo: validTo ? date.mediumMonthYear(validTo) : indefinitelyLabel,
            })}
          >
            <MdsIcon fontSize="xs" icon={MdsCalendarClock} sx={(theme) => ({ color: theme.palette.action.disabled })} />
          </Tooltip>
        )}
        <Typography sx={(theme) => ({ color: theme.palette.action.disabled })} variant="descriptionSmallSemi">
          {includeType && `${getRatelistTypeLabel(type)} - `}
          {isAdhoc ? t("Ratelist.adHoc") : getRevisionNumber(version)}
        </Typography>
      </Stack>
    </Stack>
  );
};
